import { AppData, Lightning, Log, Utils } from "@lightningjs/sdk"
import { ItemNode } from "../lib/components/grid/ItemNode"
import { Helper } from "../lib/helper"
import { Badge } from "@lightningjs/ui-components"
import { AppSetup } from "../lib/app-setup"
import { AppApiConfig } from "../lib/app-api-config"
import StarUI from './star-ui'
import VideoFeedbackControls from "./video-feedback-controls"

export interface VideoMetadataUITemplateSpec extends Lightning.Component.TemplateSpec {
    InfoTextWrapper: object
    item: ItemNode
}

export default class VideoMetadataUI
    extends Lightning.Component<VideoMetadataUITemplateSpec>
    implements Lightning.Component<VideoMetadataUITemplateSpec> {

    currentItem: ItemNode | null = null
    pendingLogoUrl: string | null = null

    static override _template(): Lightning.Component.Template<VideoMetadataUITemplateSpec> {
        return {
            collision: true,
            InfoTextWrapper: {
                x: 0,
                y: 0,
                collision: true,
                flex: { direction: 'column' },
                Title: { text: { text: '', fontFace: Helper.getFontFamily('Black'), fontSize: 42, wordWrapWidth: 900, maxLines: 2, maxLinesSuffix: '...', textOverflow: 'ellipsis' } },
                ContentLogo: { visible: false, flexItem: { marginBottom: 10 } },
                MoodsTag: { text: { text: '', fontSize: 26, fontFace: Helper.getFontFamily('Regular'), textColor: 0xffffffff } },
                ItemMetadataWrapper: {
                    y: 10,
                    flex: { direction: 'row', wrap: false },
                    rect: true, color: 0x00000000,
                    children: []
                },
                VideoFeedback: {
                    collision: true,
                    visible: false,
                    type: VideoFeedbackControls,
                    flexItem: { marginTop: 14, marginLeft: -10},
                },
            }
        }
    }

    override _init() {
        this.tag('InfoTextWrapper.ContentLogo').on('txError', this._onContentLogoError.bind(this))
    }

    /* Fallback: if the ContentLogo image errors (e.g. 404), show the text Title instead */
    _onContentLogoError() {
        if (!this.pendingLogoUrl || !this.currentItem) return
        const item = this.currentItem
        Log.error('ContentLogo failed to load, falling back to title:', this.pendingLogoUrl)
        this.pendingLogoUrl = null
        this.patch({
            InfoTextWrapper: {
                Title: { visible: true, text: { text: item.title }, color: 0xffffffff },
                ContentLogo: { visible: false },
            },
        })
    }

    getPatchObjects(item: ItemNode): any {
        const { appState } = AppData as AppSetup;
        const appApiConfig: AppApiConfig = appState?.getAppApiConfig()!;
        const bgcolor = Helper.getHexaColor(appApiConfig?.appLayoutConfig.bgcolor)
        const textcolor = Helper.getHexaColor(appApiConfig?.appLayoutConfig.textcolor, 0.5)
        const likeImg = appApiConfig?.appConfig.likeImg;
        const dislikeImg = appApiConfig?.appConfig.dislikeImg;
        const loveitImg = appApiConfig?.appConfig.loveitImg

        const infoObj: any = {
            ItemMetadataWrapper: {
                children: []
            },
            MoodsTag: { text: { text: item.mood_tags ? item.mood_tags.split(',').join('  •  ') : ''}}
        }

        this.currentItem = item
        const logoUrl = (item.content_logo || '').trim()
        if (appApiConfig?.appConfig?.enableContentLogo && logoUrl) {
            this.pendingLogoUrl = logoUrl
            infoObj.Title = { visible: false }
            infoObj.ContentLogo = {
                visible: true,
                texture: {
                    src: logoUrl,
                    resizeMode: { type: 'contain', h: 70 },
                    type: Lightning.textures.ImageTexture,
                },
            }
        } else {
            this.pendingLogoUrl = null
            infoObj.Title = { visible: true, text: { text: item.title }, color: 0xffffffff }
            infoObj.ContentLogo = { visible: false }
        }

        const ageRatingAlpha = (item.age_appropriate_rating == '' ? 0 : 1)
        const airingPeriodAlpha = (item.airing_period == null || item.airing_period === '' ? 0 : 1)
        const runtimeAlpha = (item.runtime == 0 || airingPeriodAlpha == 1 ? 0 : 1)
        const popularityRatingAlpha = (item.popularity_rating == -1 ? 0 : 1)
        const releaseDate = item.release_date
        const likeDisLikeAlpha = (item.likedislike == null ? 0 : Number(item.likedislike))

        const releaseDateRuntime = [
            releaseDate !== '' ? releaseDate : null,
            runtimeAlpha == 1 ? Helper.secondsToHms(item.runtime!) : null,
        ].filter((v): v is string => v !== null).join(' • ')

        if (releaseDateRuntime !== ''  && airingPeriodAlpha == 0) {
            infoObj.ItemMetadataWrapper.children.push({
                h: 50,
                flexItem: { marginLeft: 0, marginRight: 35 },
                text: { alpha: 0.5, text: releaseDateRuntime, fontSize: 24, fontFace: Helper.getFontFamily('Light'), textColor: Helper.getHexaColor("ffffff", 0.5), },
            })
        }
        if (airingPeriodAlpha == 1) {
            infoObj.ItemMetadataWrapper.children.push({
                h: 40,
                flexItem: { marginLeft: 0, marginRight: 35 },
                text: { text: item.airing_period, fontSize: 24, fontFace: Helper.getFontFamily('Regular'), textColor: Helper.getHexaColor("ffffff", 0.5) },
            })
        }
        if (ageRatingAlpha == 1) {
            infoObj.ItemMetadataWrapper.children.push({
                flexItem: { marginLeft: 0, marginRight: 35 },
                type: Badge,
                title: item.age_appropriate_rating, style: { paddingX: 16, paddingY: 3, strokeWidth: 0, backgroundColor: textcolor, radius: 0, textStyle: { fontSize: 20, textColor: bgcolor, fontFace: Helper.getFontFamily('Regular') } }
            })
        }

        const ccIcon = Helper.getCCIcon(item);
        if (ccIcon !== '') {
            infoObj.ItemMetadataWrapper.children.push({
                flexItem: { marginLeft: 0, marginRight: 35 },
                color: Helper.getHexaColor("ffffff", 0.5),
                texture: {
                    src: Utils.asset(ccIcon),
                    type: Lightning.textures.ImageTexture
                }
            })
        }

        const isMultiLangEnabled = Helper.hasSupportedMultiLangDescription(item);

        if (isMultiLangEnabled) {
            infoObj.ItemMetadataWrapper.children.push({
                flexItem: { marginLeft: 0, marginRight: 35 },
                color: Helper.getHexaColor("ffffff", 0.5),
                texture: {
                    src: Utils.asset('images/ico-multi-lang.png'),
                    type: Lightning.textures.ImageTexture
                }
            })
        }


        if (popularityRatingAlpha == 1) {
            infoObj.ItemMetadataWrapper.children.push({
                alpha: popularityRatingAlpha,
                type: StarUI,
                rating: item.popularity_rating,
            })
        }

        return infoObj
    }

    get feedbackTag() {
        return (this.tag('InfoTextWrapper') as any).tag('VideoFeedback') as VideoFeedbackControls;
    }

    setVideoFeedback(nodeId: string, likeDislike: any) {
        const fb = this.feedbackTag;
        fb.setVideoItem(nodeId, likeDislike);
        fb.patch({ visible: true });
    }

    set item(item: ItemNode) {
        if (!item) return;
        this.patch({
            InfoTextWrapper: this.getPatchObjects(item),
        })
    }
}
